import { AiOutlineEye } from "react-icons/ai";
import React, { useState, useEffect } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { Visibility} from "@mui/icons-material";
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid } from "@mui/x-data-grid";
import { Modal, Button, Tag, Popover, Input, Select } from "antd";
import { WarningOutlined, SearchOutlined, EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getClientTasks, deleteTask } from "../../../redux/actions/authActions";
import CustomNoRowsOverlay from "../../../components/customNoRowsOverlay";

// Helper to extract and normalize all locations from a task row
const parseTaskLocations = (row) => {
  let list = [];
  if (row?.locations && Array.isArray(row.locations) && row.locations.length > 0) {
    list = row.locations.filter(
      (l) => (l.state && l.state.trim()) || (l.location && l.location.trim())
    );
  }
  if (list.length === 0 && (row?.state?.trim() || row?.location?.trim())) {
    list = [{ state: row.state?.trim() || "", location: row.location?.trim() || "" }];
  }
  return list;
};

// Helper to group locations by state for clean presentation
const groupLocationsByState = (locations) => {
  const grouped = {};
  locations.forEach((l) => {
    const st = l.state?.trim() || "Unspecified State";
    const loc = l.location?.trim() || "All Branches";
    if (!grouped[st]) grouped[st] = [];
    if (!grouped[st].includes(loc)) grouped[st].push(loc);
  });
  return grouped;
};

const MemberTaskHome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("clientId"); // Get clientId from URL params

  const [rows, setRows] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [fileType, setFileType] = useState("");
  const [locationModalData, setLocationModalData] = useState(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");

  const dispatch = useDispatch();
  const clientTasks = useSelector((state) => state.auth.clientTasks);

  const isValidDate = (date) => {
    return date && !isNaN(new Date(date).getTime());
  };

  useEffect(() => {
    async function fetchTasks() {
      if (clientId) {
        try {
          await dispatch(getClientTasks(clientId));
        } catch (err) {
          console.log(err);
        }
      }
    }
    fetchTasks();
  }, [clientId, dispatch]);

  // Extract available years from task due dates
  const availableYears = React.useMemo(() => {
    const yearSet = new Set();
    const currentYear = new Date().getFullYear();
    // Include past years back to 2010
    for (let yr = 2010; yr <= currentYear; yr++) {
      yearSet.add(yr);
    }
    // Also include any other years from task records
    (clientTasks || []).forEach((t) => {
      if (t.dueDate) {
        const yr = new Date(t.dueDate).getFullYear();
        if (!isNaN(yr)) yearSet.add(yr);
      }
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [clientTasks]);

  // Filter tasks based on search input and selected year
  const filteredTasks = React.useMemo(() => {
    return (clientTasks || []).filter((task) => {
      const q = taskSearchQuery.toLowerCase().trim();
      const taskName = task.service?.name || task.name || "";
      const taskId = task.id || "";
      const assignees = (task.assignees || []).map((a) => a.name).join(" ");
      const state = task.state || "";
      const location = task.location || "";

      const matchesSearch =
        !q ||
        taskName.toLowerCase().includes(q) ||
        taskId.toLowerCase().includes(q) ||
        assignees.toLowerCase().includes(q) ||
        state.toLowerCase().includes(q) ||
        location.toLowerCase().includes(q);

      if (!matchesSearch) return false;

      if (selectedYear !== "all") {
        if (!task.dueDate) return false;
        const yr = new Date(task.dueDate).getFullYear();
        if (yr !== Number(selectedYear)) return false;
      }

      return true;
    });
  }, [clientTasks, taskSearchQuery, selectedYear]);

  useEffect(() => {
    setRows(filteredTasks);
  }, [filteredTasks]);

  const handleViewTask = (id) => {
    navigate(`/task-management/view-client/tasks?clientId=${clientId}&taskId=${id}`);
  };

  const handleDeleteTask = async (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this task?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(deleteTask(id));
          await dispatch(getClientTasks(clientId)); // Refresh list
        } catch (error) {
          console.error("Delete task error:", error);
        }
      },
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Upcoming":
        return { color: "#0080ff", fontWeight: "bold" };
      case "Completed":
        return { color: "green", fontWeight: "bold" };
      case "In Progress":
        return { color: "orange", fontWeight: "bold" };
      case "Pending":
        return { color: "#ff6600", fontWeight: "bold" };
      default:
        return {};
    }
  };

  const columns = [
    { field: "id", headerName: "Task ID", width: 120 },
    {
      field: "service",
      headerName: "Task Name",
      width: 220,
      renderCell: (params) => {
        const row = params.row;
        const hasTeamEscalation = row?.teamDelayReason && row.teamDelayReason.trim() !== "";
        const hasClientEscalation = row?.clientDelayReason && row.clientDelayReason.trim() !== "";
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 4 }}>
            <span style={{ fontWeight: 500 }}>{params?.value?.name || row.name}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {hasTeamEscalation && (
                <Tag color="red" style={{ fontSize: 9, padding: '0 4px', lineHeight: '16px' }}>
                  <WarningOutlined /> Team Alert
                </Tag>
              )}
              {hasClientEscalation && (
                <Tag color="orange" style={{ fontSize: 9, padding: '0 4px', lineHeight: '16px' }}>
                  <WarningOutlined /> Client Report
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 150,
      renderCell: (params) => (
        <p
          style={{
            color: params.value === "02/02/24" ? "red" : "black",
            fontWeight: params.value === "02/02/24" ? "bold" : "normal",
          }}
        >
          {isValidDate(params.value) ? new Date(params.value).toDateString() : "No Data"}
        </p>
      ),
    },
    {
      field: "dueStatus",
      headerName: "Due Status",
      width: 170,
      renderCell: (params) => {
        const row = params.row;
        if (row.status?.toLowerCase() === "completed") {
          return <span style={{ color: "#16a34a", fontWeight: "600" }}>Completed</span>;
        }
        if (!row.dueDate) return "-";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(row.dueDate);
        due.setHours(0, 0, 0, 0);
        const diffTime = due - today;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
          const days = Math.abs(diffDays);
          return (
            <span style={{ color: "#dc2626", fontWeight: "bold" }}>
              Overdue ({days} {days === 1 ? "day" : "days"})
            </span>
          );
        }
        if (diffDays === 0) {
          return (
            <span style={{ color: "#d97706", fontWeight: "bold" }}>
              Due Today
            </span>
          );
        }
        if (diffDays === 1) {
          return (
            <span style={{ color: "#ea580c", fontWeight: "bold" }}>
              Due Tomorrow
            </span>
          );
        }
        if (diffDays <= 3) {
          return (
            <span style={{ color: "#f59e0b", fontWeight: "bold" }}>
              Due in {diffDays} days
            </span>
          );
        }
        return <span style={{ color: "#16a34a", fontWeight: "500" }}>On Track</span>;
      },
    },
    {
      field: "state",
      headerName: "State",
      width: 180,
      renderCell: (params) => {
        const row = params.row;
        const locations = parseTaskLocations(row);
        const states = [...new Set(locations.map((l) => l.state?.trim()).filter(Boolean))];

        if (states.length === 0) return <span>-</span>;
        if (states.length <= 2) {
          return (
            <div className="flex flex-wrap gap-1 items-center h-full py-1">
              {states.map((st, i) => (
                <Tag key={i} color="blue" className="!mr-1 !mb-0 text-xs">
                  {st}
                </Tag>
              ))}
            </div>
          );
        }

        const taskName = row.service?.name || row.name || "Task";

        return (
          <div className="flex flex-wrap gap-1 items-center h-full py-1">
            {states.slice(0, 2).map((st, i) => (
              <Tag key={i} color="blue" className="!mr-1 !mb-0 text-xs">
                {st}
              </Tag>
            ))}
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div style={{ minWidth: 240, maxWidth: 320 }} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between pb-1.5 mb-2 border-b border-gray-100">
                    <span className="font-bold text-xs text-gray-700">
                      Assigned States ({states.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setLocationSearchQuery("");
                        setLocationModalData({ taskName, locations });
                      }}
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold cursor-pointer underline"
                    >
                      Full Table
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto">
                    {states.map((st, i) => (
                      <Tag key={i} color="blue" className="text-xs">
                        {st}
                      </Tag>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="processing" className="!mr-0 !mb-0 text-xs font-semibold cursor-pointer hover:opacity-80">
                +{states.length - 2} more
              </Tag>
            </Popover>
          </div>
        );
      },
    },
    {
      field: "location",
      headerName: "Location / Branches",
      width: 230,
      renderCell: (params) => {
        const row = params.row;
        const locations = parseTaskLocations(row);
        const validBranches = locations.filter((l) => l.location?.trim() || l.state?.trim());

        if (validBranches.length === 0) return <span>-</span>;

        if (validBranches.length <= 2) {
          return (
            <div className="flex flex-wrap gap-1 items-center h-full py-1">
              {validBranches.map((l, i) => (
                <Tooltip
                  key={i}
                  title={l.state ? `${l.location || 'Branch'} (${l.state})` : (l.location || l.state)}
                  arrow
                >
                  <Tag color="cyan" className="!mr-1 !mb-0 text-xs">
                    {l.location || l.state}
                  </Tag>
                </Tooltip>
              ))}
            </div>
          );
        }

        const taskName = row.service?.name || row.name || "Task";
        const grouped = groupLocationsByState(validBranches);

        return (
          <div className="flex flex-wrap gap-1 items-center h-full py-1">
            {validBranches.slice(0, 2).map((l, i) => (
              <Tooltip
                key={i}
                title={l.state ? `${l.location || 'Branch'} (${l.state})` : (l.location || l.state)}
                arrow
              >
                <Tag color="cyan" className="!mr-1 !mb-0 text-xs">
                  {l.location || l.state}
                </Tag>
              </Tooltip>
            ))}
            <Popover
              trigger={["hover", "click"]}
              placement="bottom"
              content={
                <div style={{ minWidth: 260, maxWidth: 340 }} onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between pb-1.5 mb-2 border-b border-gray-100">
                    <span className="font-bold text-xs text-gray-700">
                      Cities & Branches ({validBranches.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setLocationSearchQuery("");
                        setLocationModalData({ taskName, locations });
                      }}
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-semibold cursor-pointer underline"
                    >
                      Full Table
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {Object.entries(grouped).map(([st, cList], idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded border border-gray-100">
                        <div className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1">
                          {st} ({cList.length})
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cList.map((city, cIdx) => (
                            <Tag key={cIdx} color="default" className="text-xs bg-white">
                              {city}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
            >
              <Tag color="geekblue" className="!mr-0 !mb-0 text-xs font-bold cursor-pointer hover:opacity-80">
                +{validBranches.length - 2} more
              </Tag>
            </Popover>
          </div>
        );
      },
    },
    { field: "assignees", headerName: "Assignees", width: 150, renderCell: (params) => params?.value?.map(a => a.name)?.join(", ") },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <div style={getStatusStyle(params.value)}>{params.value}</div>
      ),
    },
    {
      field: "document",
      headerName: "Document",
      width: 160,
      renderCell: (params) => {
        const row = params.row;
        const mainUrl = row.document;
        const subDocs = row.documents || [];
        
        // Use first document from list or the singular document
        const url = (subDocs.length > 0 ? subDocs[0].url : mainUrl) || null;

        if (!url) {
          return <span className="text-gray-400 italic text-sm pt-3 block">No document</span>;
        }

        return (
          <Button
            type="link"
            onClick={() => {
              const extension = url.split("?")[0].split(".").pop().toLowerCase();
              if (extension === "pdf") {
                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.click();
              } else {
                setPreviewUrl(url);
                setFileType(extension);
                setPreviewVisible(true);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              paddingTop: "15px",
            }}
          >
            View <AiOutlineEye />
          </Button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="View Task">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewTask(params.row._id)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
         <Tooltip title="Delete Task">
  <IconButton
    size="small"
    color="error"
    onClick={() => handleDeleteTask(params.row._id)}
  >
    <DeleteIcon />
  </IconButton>
</Tooltip>
        </div>
      ),
    },
  ];

  const filteredModalLocations = React.useMemo(() => {
    if (!locationModalData?.locations) return [];
    if (!locationSearchQuery.trim()) return locationModalData.locations;
    const q = locationSearchQuery.toLowerCase();
    return locationModalData.locations.filter(
      (item) =>
        (item.state && item.state.toLowerCase().includes(q)) ||
        (item.location && item.location.toLowerCase().includes(q))
    );
  }, [locationModalData, locationSearchQuery]);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 w-full">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold !mb-0 text-gray-800">Tasks</h2>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search input */}
          <div className="relative w-64 md:w-80">
            <input
              type="text"
              value={taskSearchQuery}
              onChange={(e) => setTaskSearchQuery(e.target.value)}
              placeholder="Search task, assignee, state, city..."
              className="w-full py-1.5 pl-9 pr-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
            />
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
          </div>

          {/* Year Filter */}
          <div className="flex items-center gap-1.5">
            <CalendarOutlined className="text-gray-500 text-sm" />
            <span className="text-xs font-semibold text-gray-600">Year:</span>
            <Select
              showSearch
              value={selectedYear}
              onChange={(val) => setSelectedYear(val)}
              style={{ width: 120 }}
              size="middle"
              filterOption={(input, option) =>
                String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              <Select.Option value="all">All Years</Select.Option>
              {availableYears.map((yr) => (
                <Select.Option key={yr} value={String(yr)}>
                  {yr}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="h-[500px]">
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id || row.id}
          pageSizeOptions={[5, 8, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 8, page: 0 } },
          }}
          pagination
          rowHeight={64}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }} // Custom no rows overlay
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f9fafb",
              fontWeight: "bold",
              fontSize: "0.875rem",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f3f4f6",
            },
            border: "none", // Removes internal border for cleaner look
          }}
        />
      </div>

      {/* Document Preview Modal */}
      <Modal
        title={<span className="text-xl font-bold text-blue-600">Document Preview</span>}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)} className="rounded-full">
            Close
          </Button>
        ]}
        width={800}
        centered
      >
        <div className="mt-4">
          {["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(fileType) ? (
            <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain rounded-md shadow-md" />
            </div>
          ) : fileType === "pdf" ? (
            <iframe src={`${previewUrl}#toolbar=0`} title="PDF Preview" width="100%" height="600px" className="border-0 rounded-lg shadow-inner" />
          ) : (
            <div className="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <p className="text-gray-500 mb-4 font-medium italic">Preview not available for this file type ({fileType}).</p>
              <Button type="primary" href={previewUrl} target="_blank" download className="bg-blue-600 hover:bg-blue-700 rounded-full px-8 shadow-md border-0">
                Download Document
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {/* ── ALL LOCATIONS BREAKDOWN MODAL ──────────────────────────────── */}
      <Modal
        open={Boolean(locationModalData)}
        onCancel={() => {
          setLocationModalData(null);
          setLocationSearchQuery("");
        }}
        footer={null}
        width={650}
        title={
          <div className="flex items-center gap-2 text-base font-bold text-gray-800 border-b border-gray-100 pb-3">
            <EnvironmentOutlined className="text-blue-600" />
            <span>Assigned Locations & Branches</span>
          </div>
        }
      >
        {locationModalData && (
          <div className="pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
              <div>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider block">
                  Task Name
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {locationModalData.taskName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Tag color="blue" className="!m-0 text-xs font-semibold">
                  {[...new Set(locationModalData.locations.map((l) => l.state?.trim()).filter(Boolean))].length} States
                </Tag>
                <Tag color="cyan" className="!m-0 text-xs font-semibold">
                  {locationModalData.locations.filter((l) => l.location?.trim() || l.state?.trim()).length} Branches
                </Tag>
              </div>
            </div>

            {/* Quick Search */}
            <div className="mb-3">
              <Input
                placeholder="Search state or city..."
                prefix={<SearchOutlined className="text-gray-400" />}
                value={locationSearchQuery}
                onChange={(e) => setLocationSearchQuery(e.target.value)}
                allowClear
                className="rounded-lg"
              />
            </div>

            {/* List Table */}
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-100 text-gray-600 sticky top-0 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-2.5 px-3 w-12 font-bold text-center">#</th>
                    <th className="py-2.5 px-3 font-bold">State</th>
                    <th className="py-2.5 px-3 font-bold">Location / Branch City</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredModalLocations.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-6 text-gray-400 italic">
                        {locationSearchQuery ? "No locations matching your search." : "No locations recorded."}
                      </td>
                    </tr>
                  ) : (
                    filteredModalLocations.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-3 text-center text-gray-400 font-medium">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-3">
                          <Tag color="blue" className="!m-0 font-medium">
                            {item.state || "Unspecified"}
                          </Tag>
                        </td>
                        <td className="py-2.5 px-3 font-medium text-gray-700">
                          {item.location ? (
                            <Tag color="cyan" className="!m-0">
                              {item.location}
                            </Tag>
                          ) : (
                            <span className="text-gray-400 italic">All branches</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MemberTaskHome;
